/* eslint-disable react/no-array-index-key */
import { FC } from 'react';
import { twJoin } from 'tailwind-merge';

import Icon from 'molecules/Icon';
import Image from 'molecules/Image';

import FeatureTableCell from 'components/Tables/FeatureComparisonTable/components/FeatureTableCell';
import { FeatureComparisonTableConfigProps } from 'components/Tables/FeatureComparisonTable/config';

import { hasArrayValues } from 'utils/array/arrays';

interface MobileFeatureCardProps extends Pick<FeatureComparisonTableConfigProps, 'tableSections'> {
  title: string;
  columnKey: 'columnOne' | 'columnTwo' | 'columnThree';
  isHighlighted?: boolean;
  isPrismatic?: boolean;
  isOpen: boolean;
  onToggle: () => void;
}

const MobileFeatureCard: FC<MobileFeatureCardProps> = ({
  tableSections,
  title,
  columnKey,
  isHighlighted,
  isPrismatic,
  isOpen,
  onToggle,
}) => {
  const allRows = tableSections?.flatMap(section => section.rows || []) || [];

  const showGradientHeader = isPrismatic || isHighlighted;

  const containerClasses = twJoin(
    'relative flex w-full flex-col overflow-hidden backdrop-blur-md',
    'bg-[radial-gradient(ellipse_98.08%_31.01%_at_50.00%_49.88%,_rgba(255,_255,_255,_0.03)_0%,_rgba(255,_255,_255,_0)_100%)]',
    'border border-white/20',
    isPrismatic ? 'rounded-t-xl' : 'rounded-none',
  );

  return (
    <div className={containerClasses}>
      {showGradientHeader && (
        <div className="pointer-events-none absolute left-0 top-0 h-16 w-full rounded-t-xl bg-gradient-prismatic opacity-30" />
      )}

      <button
        type="button"
        onClick={onToggle}
        className="relative z-10 flex h-16 w-full items-center justify-between px-[29px] text-left"
      >
        {isPrismatic ? (
          <>
            <div className="flex items-center">
              <Image url="/prismatic-logo.svg" alt="Prismatic" className="h-[29px] w-[143px] object-contain" />
              <span className="sr-only">Prismatic</span>
            </div>
            <Icon
              icon="chevron-up"
              size={24}
              className={twJoin(
                'text-primary-400 transition-transform duration-200',
                isOpen ? 'rotate-0' : 'rotate-180',
              )}
            />
          </>
        ) : (
          <>
            <span className="font-spaceGrotesk text-2xl font-medium leading-8 text-white">{title}</span>
            <Icon
              icon="chevron-down"
              size={24}
              className={twJoin(
                'text-primary-400 transition-transform duration-200',
                isOpen ? 'rotate-180' : 'rotate-0',
              )}
            />
          </>
        )}
      </button>

      <div
        className={twJoin(
          'w-full overflow-hidden border-t border-white/20 transition-all duration-300 ease-out',
          isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0',
        )}
      >
        <div className="flex flex-col">
          {hasArrayValues(allRows) &&
            allRows.map((row, idx) => {
              const cellData = row[columnKey];

              return (
                <div key={`${columnKey}-${idx}`}>
                  <div className="flex min-h-[105px] items-center justify-between px-[29px] py-5">
                    <div className="w-40 shrink-0">
                      <span className="leading-6 text-gray-300">{row.rowHeading}</span>
                    </div>

                    {cellData && (
                      <div className="shrink-0">
                        <FeatureTableCell iconSize={25} {...cellData} />
                      </div>
                    )}
                  </div>
                  {idx < allRows.length - 1 && <div className="h-0 w-full border-t border-white/20" />}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default MobileFeatureCard;
